import React, { useEffect, useState } from 'react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import { Target, Plus, Play, X, ChevronRight, Award, CheckCircle2, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const STATUS_COLOR = {
  Draft: 'bg-slate-100 text-slate-700',
  SelfReview: 'bg-teal-100 text-teal-700',
  ManagerReview: 'bg-amber-100 text-amber-700',
  HODReview: 'bg-violet-100 text-violet-700',
  HRReview: 'bg-emerald-100 text-emerald-700',
  Closed: 'bg-slate-200 text-slate-600',
};

export default function PMS() {
  const { user } = useAuth();
  const [tab, setTab] = useState('appraisals');
  const [cycles, setCycles] = useState([]);
  const [appraisals, setAppraisals] = useState([]);
  const [scope, setScope] = useState('mine');
  const [cycleId, setCycleId] = useState('');
  const [openId, setOpenId] = useState(null);
  const [showCycleModal, setShowCycleModal] = useState(false);

  const isHR = user?.department === 'HR' || user?.department === 'MD' || user?.role === 'super_admin';

  const loadCycles = async () => {
    try { const r = await apiGet('/pms/cycles'); setCycles(r.cycles || []); }
    catch { /* */ }
  };
  const loadAppraisals = async () => {
    try {
      const url = `/pms/appraisals?scope=${scope}${cycleId ? `&cycle_id=${cycleId}` : ''}`;
      const r = await apiGet(url);
      setAppraisals(r.appraisals || []);
    } catch { /* */ }
  };
  useEffect(() => { loadCycles(); }, []);
  useEffect(() => { loadAppraisals(); }, [scope, cycleId]);

  return (
    <div className="p-6 space-y-6" data-testid="pms-page">
      <PageHeader title="Performance Management" subtitle="Run appraisal cycles · set KRAs · self / manager / HOD / HR reviews · final ratings & increments." icon={Target} />

      {/* Tabs */}
      <div className="border-b border-slate-200 flex gap-6">
        {[
          { k: 'appraisals', label: 'Appraisals' },
          { k: 'cycles', label: 'Cycles' },
        ].map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`pb-3 text-xs font-display font-bold uppercase tracking-[0.15em] border-b-2 -mb-px ${tab === t.k ? 'border-[#1F7A6E] text-[#1F7A6E]' : 'border-transparent text-slate-500 hover:text-[#1F7A6E]'}`}
            data-testid={`pms-tab-${t.k}`}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'cycles' && (
        <div className="space-y-4">
          {isHR && (
            <button onClick={() => setShowCycleModal(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs uppercase tracking-wider font-display font-bold rounded-sm" data-testid="create-cycle-btn">
              <Plus size={14} /> New Cycle
            </button>
          )}
          <div className="bg-white border border-slate-200 rounded-sm">
            <table className="w-full dense-table">
              <thead>
                <tr><th>Name</th><th>FY</th><th>Type</th><th>Period</th><th>Status</th><th>Appraisals</th><th></th></tr>
              </thead>
              <tbody>
                {cycles.map(c => (
                  <tr key={c.id} data-testid={`cycle-row-${c.id}`}>
                    <td className="font-semibold">{c.name}</td>
                    <td className="text-xs">{c.fy}</td>
                    <td className="text-xs">{c.cycle_type}</td>
                    <td className="text-xs">{c.start_date} → {c.end_date}</td>
                    <td><span className={`tag ${STATUS_COLOR[c.status] || ''}`}>{c.status}</span></td>
                    <td className="text-xs">{c.appraisal_count || 0}</td>
                    <td>
                      {isHR && c.status === 'Draft' && (
                        <button onClick={() => launchCycle(c.id, loadCycles)} className="inline-flex items-center gap-1 text-xs text-[#1F7A6E] hover:underline" data-testid={`launch-cycle-${c.id}`}>
                          <Play size={12} /> Launch
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {cycles.length === 0 && <tr><td colSpan="7" className="text-center text-sm text-slate-500 py-6">No cycles yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'appraisals' && (
        <>
          <div className="flex flex-wrap gap-3 items-center">
            <div className="inline-flex border border-slate-200 rounded-sm">
              {['mine', 'team', 'all'].map(s => (
                <button
                  key={s}
                  onClick={() => setScope(s)}
                  className={`px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider ${scope === s ? 'bg-[#1F7A6E] text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                  data-testid={`pms-scope-${s}`}
                >{s === 'mine' ? 'Mine' : s === 'team' ? 'My Team' : 'All'}</button>
              ))}
            </div>
            <select value={cycleId} onChange={e => setCycleId(e.target.value)} className="px-3 py-1.5 border border-slate-200 rounded-sm text-sm" data-testid="cycle-filter">
              <option value="">All Cycles</option>
              {cycles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <table className="w-full dense-table">
              <thead>
                <tr><th>Employee</th><th>Department</th><th>Cycle</th><th>Status</th><th>Self</th><th>Manager</th><th>Final</th><th></th></tr>
              </thead>
              <tbody>
                {appraisals.map(a => (
                  <tr key={a.id} onClick={() => setOpenId(a.id)} className="cursor-pointer hover:bg-slate-50" data-testid={`appraisal-row-${a.id}`}>
                    <td>
                      <div className="font-semibold">{a.employee_name}</div>
                      <div className="text-xs text-slate-500">{a.employee_id} · {a.designation}</div>
                    </td>
                    <td className="text-xs">{a.department}</td>
                    <td className="text-xs">{a.cycle_name}</td>
                    <td><span className={`tag ${STATUS_COLOR[a.status] || ''}`}>{a.status}</span></td>
                    <td className="text-xs">{a.scores?.self ?? '—'}</td>
                    <td className="text-xs">{a.scores?.manager ?? '—'}</td>
                    <td className="text-xs font-bold">{a.scores?.final ?? '—'}</td>
                    <td><ChevronRight size={14} className="text-slate-400" /></td>
                  </tr>
                ))}
                {appraisals.length === 0 && <tr><td colSpan="8" className="text-center text-sm text-slate-500 py-6">No appraisals in this view.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {openId && <AppraisalDrawer id={openId} onClose={() => setOpenId(null)} onUpdated={loadAppraisals} isHR={isHR} userEmpId={user?.employee_id} />}
      {showCycleModal && <CycleModal onClose={() => setShowCycleModal(false)} onCreated={loadCycles} />}
    </div>
  );
}

async function launchCycle(id, reload) {
  if (!window.confirm('Launch this cycle? An appraisal will be created for every active employee.')) return;
  try {
    const r = await apiPost(`/pms/cycles/${id}/launch`);
    toast.success(`Launched · ${r.appraisals_created} appraisals created`);
    reload();
  } catch (err) {
    toast.error(err.response?.data?.detail || 'Failed to launch');
  }
}

// ==================== Cycle Modal ====================
function CycleModal({ onClose, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    name: 'Annual FY2026 Appraisal',
    fy: 'FY2026',
    cycle_type: 'Annual',
    start_date: today,
    end_date: today,
    description: '',
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await apiPost('/pms/cycles', form);
      toast.success('Cycle created — Launch it to seed appraisals.');
      onCreated(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="cycle-modal">
      <div className="bg-white rounded-sm max-w-lg w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h3 className="font-display font-bold text-xl">New Appraisal Cycle</h3><button onClick={onClose}><X size={18} /></button></div>
        <Inp label="Cycle Name *" value={form.name} onChange={v => setForm({ ...form, name: v })} testid="cycle-name" />
        <div className="grid grid-cols-2 gap-3">
          <Inp label="FY *" value={form.fy} onChange={v => setForm({ ...form, fy: v })} testid="cycle-fy" />
          <div>
            <label className="text-xs font-display font-bold uppercase tracking-wider text-slate-600 block mb-1">Type *</label>
            <select value={form.cycle_type} onChange={e => setForm({ ...form, cycle_type: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm" data-testid="cycle-type">
              <option>Annual</option><option>MidYear</option>
            </select>
          </div>
          <Inp label="Start *" value={form.start_date} onChange={v => setForm({ ...form, start_date: v })} type="date" testid="cycle-start" />
          <Inp label="End *" value={form.end_date} onChange={v => setForm({ ...form, end_date: v })} type="date" testid="cycle-end" />
        </div>
        <button onClick={submit} disabled={busy} className="w-full px-4 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs uppercase tracking-wider font-display font-bold rounded-sm disabled:opacity-50" data-testid="save-cycle">
          {busy ? 'Saving…' : 'Create Cycle'}
        </button>
      </div>
    </div>
  );
}

// ==================== Appraisal Drawer ====================
function AppraisalDrawer({ id, onClose, onUpdated, isHR, userEmpId }) {
  const [doc, setDoc] = useState(null);
  const [goals, setGoals] = useState([]);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [hrForm, setHrForm] = useState({ final_score: '', increment_percent: '', recommendation: '', hr_comment: '' });

  const load = async () => {
    try { const r = await apiGet(`/pms/appraisals/${id}`); setDoc(r); setGoals(r.goals || []); }
    catch { toast.error('Failed to load'); }
  };
  useEffect(() => { load(); }, [id]);

  if (!doc) return null;

  const isSelf = doc.employee_id === userEmpId;
  const isManager = doc.manager_id === userEmpId;
  const stage = doc.status;
  const canSelf = isSelf && stage === 'SelfReview';
  const canManager = isManager && stage === 'ManagerReview';
  const canHOD = isHR && stage === 'HODReview';
  const canHR = isHR && stage === 'HRReview';

  const updateGoal = (i, key, val) => setGoals(prev => prev.map((g, idx) => idx === i ? { ...g, [key]: val } : g));

  const submit = async (path) => {
    setBusy(true);
    try {
      await apiPut(`/pms/appraisals/${id}/${path}`, { goals, overall_comment: comment });
      toast.success('Submitted');
      onUpdated(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };
  const finalize = async () => {
    setBusy(true);
    try {
      await apiPut(`/pms/appraisals/${id}/hr-finalize`, {
        final_score: hrForm.final_score === '' ? null : Number(hrForm.final_score),
        increment_percent: hrForm.increment_percent === '' ? null : Number(hrForm.increment_percent),
        recommendation: hrForm.recommendation || null,
        hr_comment: hrForm.hr_comment || null,
      });
      toast.success('Finalised');
      onUpdated(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const ratingKey = canSelf ? 'self_rating' : canManager ? 'manager_rating' : canHOD ? 'hod_rating' : null;
  const commentKey = canSelf ? 'self_comment' : canManager ? 'manager_comment' : canHOD ? 'hod_comment' : null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="appraisal-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-[820px] bg-slate-50 border-l border-slate-200 flex flex-col">
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between">
          <div>
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E]">{doc.cycle_name} · {doc.status}</div>
            <h2 className="font-display text-xl font-bold mt-1">{doc.employee_name} <span className="text-xs text-slate-500">({doc.employee_id})</span></h2>
            <div className="text-xs text-slate-600 mt-0.5">{doc.designation} · {doc.department} · Manager: {doc.manager_name || '—'}</div>
            <div className="flex gap-3 mt-2 text-[11px]">
              <span>Self: <b>{doc.scores?.self ?? '—'}</b></span>
              <span>Manager: <b>{doc.scores?.manager ?? '—'}</b></span>
              <span>HOD: <b>{doc.scores?.hod ?? '—'}</b></span>
              <span className="text-emerald-700">Final: <b>{doc.scores?.final ?? '—'}</b></span>
            </div>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {['kra', 'competency', 'value'].map(type => {
            const filtered = goals.map((g, idx) => ({ ...g, idx })).filter(g => g.type === type);
            if (!filtered.length) return null;
            return (
              <div key={type} className="bg-white border border-slate-200 rounded-sm">
                <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 font-display font-bold text-[10px] uppercase tracking-[0.18em] text-[#1F7A6E]">
                  {type === 'kra' ? 'KRAs (60%)' : type === 'competency' ? 'Competencies (30%)' : 'Values (10%)'}
                </div>
                <div className="divide-y divide-slate-100">
                  {filtered.map(g => (
                    <div key={g.idx} className="p-3 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          {ratingKey && type === 'kra' && canSelf ? (
                            <input value={g.title} onChange={e => updateGoal(g.idx, 'title', e.target.value)} className="w-full font-semibold text-sm border border-slate-200 rounded-sm px-2 py-1" />
                          ) : (
                            <div className="font-semibold text-sm">{g.title}</div>
                          )}
                          {g.description && <div className="text-xs text-slate-500 mt-0.5">{g.description}</div>}
                        </div>
                        <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 rounded-sm font-display font-bold">W: {g.weight}</span>
                      </div>
                      {ratingKey && (
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] uppercase tracking-wider text-slate-500 w-16">Rating</span>
                          {[1, 2, 3, 4, 5].map(r => (
                            <button
                              key={r}
                              onClick={() => updateGoal(g.idx, ratingKey, r)}
                              className={`w-8 h-8 rounded-sm border ${(g[ratingKey] === r) ? 'bg-[#1F7A6E] text-white border-[#1F7A6E]' : 'border-slate-200 hover:border-[#1F7A6E]'}`}
                              data-testid={`rating-${g.idx}-${r}`}
                            >{r}</button>
                          ))}
                        </div>
                      )}
                      {!ratingKey && (
                        <div className="flex gap-3 text-[11px] text-slate-500">
                          <span>Self: <b className="text-slate-700">{g.self_rating ?? '—'}</b></span>
                          <span>Mgr: <b className="text-slate-700">{g.manager_rating ?? '—'}</b></span>
                          <span>HOD: <b className="text-slate-700">{g.hod_rating ?? '—'}</b></span>
                        </div>
                      )}
                      {commentKey && (
                        <input value={g[commentKey] || ''} onChange={e => updateGoal(g.idx, commentKey, e.target.value)} placeholder="Add comment / evidence" className="w-full text-xs border border-slate-200 rounded-sm px-2 py-1" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {(canSelf || canManager || canHOD) && (
            <div>
              <label className="text-xs font-display font-bold uppercase tracking-wider text-slate-600 block mb-1">Overall Comment</label>
              <textarea value={comment} onChange={e => setComment(e.target.value)} rows={3} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm" placeholder="Summarise your review…" />
            </div>
          )}

          {canHR && (
            <div className="bg-white border border-emerald-200 rounded-sm p-4 space-y-3">
              <div className="font-display font-bold text-sm text-emerald-800 flex items-center gap-2"><Award size={14} /> HR Finalisation</div>
              <div className="grid grid-cols-2 gap-3">
                <Inp label="Final Score (1-5)" value={hrForm.final_score} onChange={v => setHrForm({ ...hrForm, final_score: v })} type="number" testid="hr-final-score" />
                <Inp label="Increment %" value={hrForm.increment_percent} onChange={v => setHrForm({ ...hrForm, increment_percent: v })} type="number" testid="hr-increment" />
              </div>
              <Inp label="Recommendation" value={hrForm.recommendation} onChange={v => setHrForm({ ...hrForm, recommendation: v })} testid="hr-recommendation" placeholder="Promote / Confirm / PIP / Outstanding…" />
              <textarea value={hrForm.hr_comment} onChange={e => setHrForm({ ...hrForm, hr_comment: e.target.value })} rows={2} placeholder="HR remarks" className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm" />
              <button onClick={finalize} disabled={busy} className="w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs uppercase tracking-wider font-display font-bold rounded-sm disabled:opacity-50" data-testid="finalize-btn">
                {busy ? 'Finalising…' : 'Finalise Appraisal'}
              </button>
            </div>
          )}
        </div>

        {(canSelf || canManager || canHOD) && (
          <div className="border-t border-slate-200 bg-white px-6 py-4 flex justify-end">
            <button
              onClick={() => submit(canSelf ? 'self-review' : canManager ? 'manager-review' : 'hod-review')}
              disabled={busy}
              className="inline-flex items-center gap-2 px-5 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs uppercase tracking-wider font-display font-bold rounded-sm disabled:opacity-50"
              data-testid="submit-review"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Submit {canSelf ? 'Self Review' : canManager ? 'Manager Review' : 'HOD Review'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Inp({ label, value, onChange, type = 'text', testid, placeholder }) {
  return (
    <div>
      <label className="text-xs font-display font-bold uppercase tracking-wider text-slate-600 block mb-1">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#1F7A6E]" data-testid={testid} />
    </div>
  );
}
